import { createGqlFetcher } from 'app/lib/gqlFetcher'
import { ArrowPathIcon } from '@heroicons/react/20/solid'
import { withZod } from '@remix-validated-form/with-zod'
import classNames from 'classnames'
import { compact } from 'lodash'
import { useEffect, useState } from 'react'
import { useQuery } from 'urql'
import { z } from 'zod'
import { AddNewIntegrationModal } from '../AddNewIntegrationModal'
import { RING_CENTRAL_PHONE_NUMBERS_QUERY } from '../graphql'
import { SAVE_RINGCENTRAL_INTEGRATION, REMOVE_LOCATION_INTEGRATION } from '../ops-graphql'
import { useIntegrationQuery } from '../useIntegrationQuery'
import Button from 'app/components/Button'
import { useConfirmation } from 'app/components/ConfirmationContext'
import FormComboAsync from 'app/components/form/FormComboAsync'
import FormComboMultiSelect from 'app/components/form/FormComboMultiSelect'
import FormSelect from 'app/components/form/FormComboSelect'
import { SmartForm } from 'app/components/form/SmartForm'
import Spinner from 'app/components/Spinner'
import type { RingCentralPhoneNumber } from 'app/generated/graphql'
import type { LocationJson } from 'app/types/entities'
import { closeModal, showModal } from 'app/lib/modal'
import { gqlOpsMutation } from 'app/lib/opsGraphqlClient'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { OptionType } from 'types/OptionType'

const createSchema = z.object({
  locationId: z.string().min(1, { message: `A Location is required` }),
  selection: z.array(z.object({ id: z.string() })).transform((val) => val.map((v) => ({ id: v.id }))),
})

export const createRingCentralValidator = withZod(createSchema)

type RingCentralPhoneNumbersQueryResult = {
  integrationOptions: {
    ringCentralPhoneNumbers: RingCentralPhoneNumber[]
  }
}

export function RingCentralFormModal({
  onAdd,
  currentValues,
}: {
  onAdd: () => void
  currentValues?: { locationId: string; locationName: string; ringCentralPhoneNumbers: string[] }
}) {
  const [selectedLocation, setSelectedLocation] = useState<OptionType | undefined>()
  const [selectedOptions, setSelectedOptions] = useState<OptionType[] | undefined>(undefined)
  const [confirm] = useConfirmation()

  const [{ data, fetching, error }, reexecuteQuery] = useQuery<RingCentralPhoneNumbersQueryResult>({
    query: RING_CENTRAL_PHONE_NUMBERS_QUERY,
    variables: { locationId: currentValues?.locationId },
    requestPolicy: `network-only`,
  })

  const { options, refreshing, refresh } = useIntegrationQuery<
    RingCentralPhoneNumbersQueryResult,
    RingCentralPhoneNumber
  >({
    query: RING_CENTRAL_PHONE_NUMBERS_QUERY,
    variables: { locationId: currentValues?.locationId },
    extractItems: (d) => d.integrationOptions.ringCentralPhoneNumbers,
    disabledTooltip: `This phone number is connected to another location`,
    errorMessage: `Failed to load RingCentral options`,
  })

  // Set selected options when data loads and we have current values
  useEffect(() => {
    if (options.length && currentValues?.ringCentralPhoneNumbers?.length) {
      const selectedPhoneNumbers: OptionType[] = compact(
        currentValues.ringCentralPhoneNumbers.map((phoneNumber) => {
          const opt = options.find((o) => o.id.includes(phoneNumber))
          return opt
        }),
      )
      setSelectedOptions([...selectedPhoneNumbers])
    }
  }, [options, currentValues?.ringCentralPhoneNumbers])

  const onSave = async () => {
    const locationId = currentValues?.locationId || (selectedLocation?.value as string)
    const phoneNumbers = selectedOptions?.map((opt) => opt.id) || []
    if (!locationId || !phoneNumbers.length) return

    const result = await gqlOpsMutation(SAVE_RINGCENTRAL_INTEGRATION, {
      locationId,
      phoneNumbers,
      isNew: !currentValues,
    })
    const data = result.data?.saveRingCentralIntegration
    if (data?.success) {
      notifySuccess(`RingCentral integration saved`)
      onAdd()
      closeModal()
    } else {
      notifyError(`Failed to save integration: ${data?.error || result.error || 'Unknown error'}`)
    }
  }

  const goBackToSelectIntegrationModal = () => {
    closeModal()
    showModal(
      <AddNewIntegrationModal onIntegrationAdded={onAdd} />,
      {
        title: `Add New Integration`,
        isFullScreen: false,
        overflowHidden: false,
        removePadding: true,
        useRadix: true,
      },
      false,
    )
  }

  const onRemove = () => {
    return confirm({
      title: `Remove RingCentral Integration`,
      description: `Removing this integration will also remove all associated data. This may result in contacts being removed. Do you want to continue?`,
      variant: `danger`,
    }).then(() =>
      gqlOpsMutation(REMOVE_LOCATION_INTEGRATION, {
        locationId: currentValues?.locationId!,
        integration: `ringcentral`,
      }).then((result) => {
        if (result.data?.removeLocationIntegration?.success) {
          notifySuccess(`RingCentral integration removed`)
          onAdd()
          closeModal()
        } else {
          notifyError(`Failed to remove integration: ${result.error || 'Unknown error'}`)
        }
      }),
    )
  }

  return (
    <>
      <SmartForm
        id="integration-form"
        className="!mt-0 w-[600px] grow-0 justify-start !gap-y-4 px-6 py-4"
        validator={createRingCentralValidator}
        reloadDocument={false}
        preventSubmit={true}
        onSubmit={onSave}
        hideButtons={true}
        method={currentValues ? `PUT` : `POST`}
        hiddenInputs={currentValues ? [{ name: `locationId`, value: currentValues.locationId }] : undefined}
      >
        {currentValues ? (
          <FormSelect
            cols={5}
            name="locationId"
            label="Trackable Location"
            options={[{ label: currentValues.locationName, value: currentValues.locationId }]}
            value={currentValues.locationId}
            disabled
            labelClassName="text-body-sm text-strong"
          />
        ) : (
          <FormComboAsync
            cols={5}
            name="locationId"
            required={true}
            showSearchInDropdown={true}
            label="Trackable Location"
            value={selectedLocation}
            optionsMapper={(res) =>
              res?.hits?.map((hit: LocationJson) => ({
                id: hit.id || hit.name,
                name: hit.name,
                value: hit.id,
                meta: {
                  isUsed: !!hit.ringCentralPhoneNumbers,
                  tooltipForDisabledOption: `This location is already connected to RingCentral`,
                },
              }))
            }
            fetcher={createGqlFetcher('locations')}
            labelClassName="text-body-sm text-strong"
            onChange={(val) => setSelectedLocation(val)}
            newLineError={false}
            showErrorIcon
          />
        )}
        {fetching ? (
          <div className="col-span-5 flex items-center pt-5">
            <Spinner size={5} />
          </div>
        ) : error ? (
          <div className="col-span-5 pt-5 text-sm text-red-600">Failed to load phone numbers</div>
        ) : options.length === 0 ? (
          <div className="col-span-5 pt-5 text-sm text-subtle">No phone numbers available</div>
        ) : (
          <FormComboMultiSelect
            cols={5}
            name="selection"
            label="Phone Numbers"
            values={selectedOptions}
            options={options}
            labelClassName="text-body-sm text-strong"
            onChange={(opts) => setSelectedOptions(opts)}
            newLineError={false}
            checkboxSide="left"
            showErrorIcon
          />
        )}
        <div className="col-span-1 flex items-center justify-center pt-5">
          <ArrowPathIcon
            className={classNames(`ml-2 h-5 w-5 cursor-pointer`, refreshing && `animate-spin`)}
            onClick={refresh}
          />
        </div>
      </SmartForm>
      <div className="sticky bottom-0 w-full rounded-b-xl border-t border-default bg-surface-raised px-6 py-3">
        <div className="flex items-center justify-between">
          <div>
            {currentValues ? <Button label="Remove" mode="danger" size="md" onClick={() => onRemove()} /> : null}
          </div>
          <div className="flex gap-2">
            {!currentValues ? (
              <Button label="Back" mode="white" size="md" onClick={() => goBackToSelectIntegrationModal()} />
            ) : (
              <Button label="Cancel" mode="white" size="md" onClick={() => closeModal()} />
            )}
            <Button
              type="submit"
              form="integration-form"
              label="Save"
              mode="primary"
              size="md"
              disabled={(!selectedLocation && !currentValues?.locationId) || !selectedOptions}
            />
          </div>
        </div>
      </div>
    </>
  )
}
